import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import {
  Calculator, Loader2, AlertTriangle, ArrowLeft, Lock, BookOpen,
  Sparkles, ShieldCheck, ExternalLink,
} from "lucide-react";
import { API } from "../App";

// -----------------------------------------------------------------------------
// V11 Calculator Suite — public + gated (HTTP 402 paywall)
// Source of truth: /app/backend/book3_v11/calculator_spec/CHARSTAN_CALCULATOR_SPEC_v1.2.md
// Backend: /app/backend/services/v11_calculators.py
// -----------------------------------------------------------------------------

const T = {
  en: {
    title: "Live Calculators — Brain Drain Series III",
    subtitle: "11 interactive engineering calculators aligned to v11 worked examples",
    publicSection: "Public Calculators",
    gatedSection: "Reader-Unlocked Calculators",
    backToList: "All Calculators",
    compute: "Compute",
    computing: "Computing…",
    canonicalChip: "Show canonical example",
    canonicalConfirm: "Replace your inputs with the canonical example?",
    canonicalActive: "Canonical example loaded · From",
    canonicalEdit: "Edit any value to fit your problem",
    paywallTitle: "Reader Unlock Required",
    paywallActivate: "Activate purchase code",
    inputsHeading: "Inputs",
    resultHeading: "Result",
    publicBadge: "Public",
    gatedBadge: "Reader",
    chapterRef: "From",
    fieldRequired: "required",
    chapter: "Ch",
    sigmaDirection: "Direction",
    sigmaToDpmo: "Sigma → DPMO",
    dpmoToSigma: "DPMO → Sigma",
    sigmaShift: "Apply 1.5σ shift",
    successRunPresets: "Common presets",
  },
  pt_br: {
    title: "Calculadoras ao Vivo — Brain Drain Série III",
    subtitle: "11 calculadoras de engenharia interativas alinhadas aos exemplos do v11",
    publicSection: "Calculadoras Públicas",
    gatedSection: "Calculadoras Desbloqueadas para Leitores",
    backToList: "Todas as calculadoras",
    compute: "Calcular",
    computing: "Calculando…",
    canonicalChip: "Mostrar exemplo canônico",
    canonicalConfirm: "Substituir seus valores pelo exemplo canônico?",
    canonicalActive: "Exemplo canônico carregado · De",
    canonicalEdit: "Edite qualquer valor para o seu problema",
    paywallTitle: "Desbloqueio de Leitor Necessário",
    paywallActivate: "Ativar código de compra",
    inputsHeading: "Entradas",
    resultHeading: "Resultado",
    publicBadge: "Público",
    gatedBadge: "Leitor",
    chapterRef: "Do",
    fieldRequired: "obrigatório",
    chapter: "Cap.",
    sigmaDirection: "Direção",
    sigmaToDpmo: "Sigma → DPMO",
    dpmoToSigma: "DPMO → Sigma",
    sigmaShift: "Aplicar deslocamento 1,5σ",
    successRunPresets: "Predefinições comuns",
  },
};

// -----------------------------------------------------------------------------
// Per-calculator schema: input fields + canonical example + chapter reference
// (matches the canonical worked examples in spec v1.2)
// -----------------------------------------------------------------------------

const CALC_SCHEMA = [
  // ---------- PUBLIC ----------
  {
    id: "basic_stats",
    public: true,
    title: { en: "Mean & Standard Deviation", pt_br: "Média e Desvio Padrão" },
    chapter_ref: { en: "Ch 1 §8 Example 1", pt_br: "Cap. 1 §8 Exemplo 1" },
    fields: [
      { key: "values", type: "csv", label: { en: "Measurements", pt_br: "Medições" },
        placeholder: "10.02, 10.05, 9.98, 10.01, 10.03, 9.99, 10.00, 10.04, 10.02, 9.97" },
    ],
    canonical: { values: "10.02, 10.05, 9.98, 10.01, 10.03, 9.99, 10.00, 10.04, 10.02, 9.97" },
  },
  {
    id: "cpk_ppk",
    public: true,
    title: { en: "Process Capability (Cpk / Ppk)", pt_br: "Capacidade do Processo (Cpk / Ppk)" },
    chapter_ref: { en: "Ch 6 Eq (6.2), Ch 1 Example 2", pt_br: "Cap. 6 Eq (6.2), Cap. 1 Exemplo 2" },
    fields: [
      { key: "USL", type: "number", label: { en: "Upper Spec Limit", pt_br: "Limite Superior" } },
      { key: "LSL", type: "number", label: { en: "Lower Spec Limit", pt_br: "Limite Inferior" } },
      { key: "mean", type: "number", label: { en: "Process Mean (x̄)", pt_br: "Média do Processo (x̄)" } },
      { key: "sigma", type: "number", label: { en: "Process σ", pt_br: "σ do Processo" } },
      { key: "sample_sd", type: "number", optional: true,
        label: { en: "Sample SD (optional, for Ppk)", pt_br: "Desvio amostral (opcional, p/ Ppk)" } },
    ],
    canonical: { USL: 10.05, LSL: 9.95, mean: 10.011, sigma: 0.025 },
  },
  {
    id: "sigma_dpmo",
    public: true,
    title: { en: "Sigma Level / DPMO Converter", pt_br: "Conversor Nível Sigma / DPMO" },
    chapter_ref: { en: "Ch 6 §6, Ch 12 §3", pt_br: "Cap. 6 §6, Cap. 12 §3" },
    custom: true,  // rendered with a direction toggle
    canonical: { sigma_level: 6, apply_shift: true },
  },

  // ---------- GATED ----------
  {
    id: "rss_stack", public: false, gate_chapter: "ch_07",
    title: { en: "RSS Tolerance Stack-Up", pt_br: "Empilhamento de Tolerâncias RSS" },
    chapter_ref: { en: "Ch 7 Eq (7.1), Ch 1 Example 3", pt_br: "Cap. 7 Eq (7.1), Cap. 1 Exemplo 3" },
    fields: [
      { key: "tolerances", type: "csv", label: { en: "Component Tolerances", pt_br: "Tolerâncias dos Componentes" },
        placeholder: "0.010, 0.008, 0.006" },
    ],
    canonical: { tolerances: "0.010, 0.008, 0.006" },
  },
  {
    id: "weibull", public: false, gate_chapter: "ch_08",
    title: { en: "Weibull Reliability", pt_br: "Confiabilidade de Weibull" },
    chapter_ref: { en: "Ch 8 Eq (8.1)–(8.3)", pt_br: "Cap. 8 Eq (8.1)–(8.3)" },
    fields: [
      { key: "beta", type: "number", label: { en: "Shape (β)", pt_br: "Forma (β)" } },
      { key: "eta", type: "number", label: { en: "Scale (η)", pt_br: "Escala (η)" } },
      { key: "time", type: "number", label: { en: "Time (t)", pt_br: "Tempo (t)" } },
    ],
    canonical: { beta: 2.5, eta: 15000, time: 5000 },
  },
  {
    id: "arrhenius", public: false, gate_chapter: "ch_10",
    title: { en: "Arrhenius Acceleration", pt_br: "Aceleração de Arrhenius" },
    chapter_ref: { en: "Ch 10 Eq (10.1), (10.2)", pt_br: "Cap. 10 Eq (10.1), (10.2)" },
    fields: [
      { key: "activation_energy", type: "number",
        label: { en: "Activation Energy Eₐ (kJ/mol)", pt_br: "Energia de Ativação Eₐ (kJ/mol)" } },
      { key: "temp_use", type: "number", label: { en: "Use Temp (°C)", pt_br: "Temp. de Uso (°C)" } },
      { key: "temp_accel", type: "number", label: { en: "Test Temp (°C)", pt_br: "Temp. de Teste (°C)" } },
      { key: "test_duration", type: "number", optional: true,
        label: { en: "Test Duration (optional)", pt_br: "Duração do teste (opcional)" } },
    ],
    canonical: { activation_energy: 80, temp_use: 25, temp_accel: 40 },
  },
  {
    id: "aet", public: false, gate_chapter: "ch_09_or_14",
    title: { en: "AET — Analytical Evaluation Threshold",
             pt_br: "AET — Limite de Avaliação Analítica" },
    chapter_ref: { en: "Ch 9 Eq (9.1) · USP <1663> / ICH M7",
                   pt_br: "Cap. 9 Eq (9.1) · USP <1663> / ICH M7" },
    fields: [
      { key: "sct", type: "number", label: { en: "SCT (μg/day)", pt_br: "SCT (μg/dia)" } },
      { key: "patient_dose", type: "number",
        label: { en: "Patient Dose (g/day or mL/day)", pt_br: "Dose ao Paciente (g/dia ou mL/dia)" } },
      { key: "analytical_uncertainty_factor", type: "number",
        label: { en: "Analytical Uncertainty Factor", pt_br: "Fator de Incerteza Analítica" } },
    ],
    canonical: { sct: 1.5, patient_dose: 2.0, analytical_uncertainty_factor: 2.0 },
  },
  {
    id: "gage_rr", public: false, gate_chapter: "ch_11",
    title: { en: "Gage R&R (Measurement System Analysis)",
             pt_br: "R&R do Dispositivo (Análise do Sistema de Medição)" },
    chapter_ref: { en: "Ch 11 Eq (11.2)–(11.5), Ch 1 Example 6",
                   pt_br: "Cap. 11 Eq (11.2)–(11.5), Cap. 1 Exemplo 6" },
    nested: "variances",
    fields: [
      { key: "part", type: "number", label: { en: "Part variance", pt_br: "Variância de peça" } },
      { key: "repeatability", type: "number",
        label: { en: "Repeatability variance", pt_br: "Variância de repetibilidade" } },
      { key: "reproducibility", type: "number",
        label: { en: "Reproducibility variance", pt_br: "Variância de reprodutibilidade" } },
    ],
    canonical: { variances: { part: 0.000900, repeatability: 0.000036, reproducibility: 0.000016 } },
  },
  {
    id: "success_run", public: false, gate_chapter: "ch_17",
    title: { en: "Success-Run Sample Size", pt_br: "Tamanho de Amostra Série Sucesso" },
    chapter_ref: { en: "Ch 17 Eq (17.1), Examples 1 & 2",
                   pt_br: "Cap. 17 Eq (17.1), Exemplos 1 e 2" },
    fields: [
      { key: "confidence", type: "number", step: "0.001",
        label: { en: "Confidence (0–1)", pt_br: "Confiança (0–1)" } },
      { key: "reliability", type: "number", step: "0.001",
        label: { en: "Reliability (0–1)", pt_br: "Confiabilidade (0–1)" } },
    ],
    canonical: { confidence: 0.95, reliability: 0.95 },
    presets: [
      { label: "95/95", confidence: 0.95, reliability: 0.95 },
      { label: "95/99", confidence: 0.95, reliability: 0.99 },
      { label: "99/95", confidence: 0.99, reliability: 0.95 },
      { label: "90/90", confidence: 0.90, reliability: 0.90 },
    ],
  },
  {
    id: "oee", public: false, gate_chapter: "ch_05",
    title: { en: "Overall Equipment Effectiveness (OEE)",
             pt_br: "Eficácia Geral do Equipamento (OEE)" },
    chapter_ref: { en: "Ch 5 Eq (5.1)–(5.4)", pt_br: "Cap. 5 Eq (5.1)–(5.4)" },
    fields: [
      { key: "availability", type: "number", step: "0.001",
        label: { en: "Availability (0–1)", pt_br: "Disponibilidade (0–1)" } },
      { key: "performance", type: "number", step: "0.001",
        label: { en: "Performance (0–1)", pt_br: "Desempenho (0–1)" } },
      { key: "quality", type: "number", step: "0.001",
        label: { en: "Quality (0–1)", pt_br: "Qualidade (0–1)" } },
    ],
    canonical: { availability: 0.875, performance: 0.857, quality: 0.956 },
  },
  {
    id: "supplier_scorecard", public: false, gate_chapter: "ch_16",
    title: { en: "Supplier Scorecard", pt_br: "Placar do Fornecedor" },
    chapter_ref: { en: "Ch 16 Eq (16.1), Example 1", pt_br: "Cap. 16 Eq (16.1), Exemplo 1" },
    custom: true,  // rendered with 5×2 grid (score/weight per dimension)
    canonical: {
      scores: { quality: 95, delivery: 88, cost: 82, responsiveness: 78, compliance: 90 },
      weights: { quality: 0.30, delivery: 0.25, cost: 0.20, responsiveness: 0.10, compliance: 0.15 },
    },
  },
];

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function isCanonicalEqual(inputs, canonical) {
  if (!canonical) return false;
  return JSON.stringify(inputs) === JSON.stringify(canonical);
}

function inputsHaveContent(inputs) {
  if (!inputs) return false;
  return Object.values(inputs).some((v) => {
    if (v === null || v === undefined || v === "") return false;
    if (typeof v === "object") return Object.keys(v).length > 0;
    return true;
  });
}

function deepCopy(obj) {
  return JSON.parse(JSON.stringify(obj));
}

const CPK_COLOR = (cpk) => cpk >= 1.33 ? "emerald" : cpk >= 1.0 ? "amber" : "rose";
const TIER_COLOR = (tier = "") => {
  if (tier.startsWith("A")) return "emerald";
  if (tier.startsWith("B")) return "sky";
  if (tier.startsWith("C")) return "amber";
  return "rose";
};

// Static class map — Tailwind doesn't compile dynamically-interpolated classes,
// so we keep every class literal enumerable here.
const COLOR_CLASSES = {
  emerald: {
    bg: "bg-emerald-500/10", border: "border-emerald-500/40",
    borderThin: "border-emerald-500/20", text: "text-emerald-300",
  },
  sky: {
    bg: "bg-sky-500/10", border: "border-sky-500/40",
    borderThin: "border-sky-500/20", text: "text-sky-300",
  },
  amber: {
    bg: "bg-amber-500/10", border: "border-amber-500/40",
    borderThin: "border-amber-500/20", text: "text-amber-300",
  },
  rose: {
    bg: "bg-rose-500/10", border: "border-rose-500/40",
    borderThin: "border-rose-500/20", text: "text-rose-300",
  },
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

const V11CalculatorsPage = ({ lang: langProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { calcId: urlCalcId } = useParams();
  const lang = langProp || (location.pathname.startsWith("/calculadoras") ? "pt_br" : "en");
  const t = T[lang];

  const [selectedId, setSelectedId] = useState(null);
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [computing, setComputing] = useState(false);
  const [canonicalLoaded, setCanonicalLoaded] = useState(false);
  // Deep-link: pending auto-compute flag set when ?canonical=1 lands on a
  // valid calc slug. Computed in a separate effect once inputs are populated.
  const [pendingAutoCompute, setPendingAutoCompute] = useState(false);
  // Deep-link state — last URL we applied (so browser back/forward re-applies cleanly)
  const lastAppliedUrlRef = useRef(null);

  const selected = useMemo(
    () => CALC_SCHEMA.find((c) => c.id === selectedId),
    [selectedId]
  );

  const openCalc = useCallback((id) => {
    setSelectedId(id);
    setInputs({});
    setResult(null);
    setPaywall(null);
    setCanonicalLoaded(false);
  }, []);

  const closeCalc = useCallback(() => {
    setSelectedId(null);
    setInputs({});
    setResult(null);
    setPaywall(null);
    setCanonicalLoaded(false);
  }, []);

  const loadCanonical = useCallback(() => {
    if (!selected?.canonical) return;
    if (inputsHaveContent(inputs) && !isCanonicalEqual(inputs, selected.canonical)) {
      const ok = window.confirm(t.canonicalConfirm);
      if (!ok) return;
    }
    setInputs(deepCopy(selected.canonical));
    setResult(null);
    setPaywall(null);
    setCanonicalLoaded(true);
  }, [selected, inputs, t]);

  const compute = useCallback(async () => {
    if (!selected) return;
    setComputing(true);
    setResult(null);
    setPaywall(null);
    try {
      // Coerce CSV fields to arrays before sending
      const payload = deepCopy(inputs);
      (selected.fields || []).forEach((f) => {
        if (f.type === "csv" && typeof payload[f.key] === "string") {
          // leave as string — backend accepts both
        }
      });
      const { data } = await axios.post(`${API}/calculators/compute`, {
        calculator_id: selected.id,
        inputs: payload,
        suite: "v11",
      }, { withCredentials: true });
      setResult(data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 402 && detail?.code === "GATE_LOCKED") {
        setPaywall({
          title: t.paywallTitle,
          message: lang === "pt_br" ? detail.message_pt_br : detail.message_en,
          activationUrl: lang === "pt_br" ? detail.activation_url_pt_br : detail.activation_url,
          gateChapter: detail.gate_chapter,
        });
      } else if (status === 400 && detail) {
        setResult({ error: lang === "pt_br" ? detail.message_pt_br : detail.message_en });
      } else {
        setResult({ error: err.response?.data?.detail || err.message || "Error" });
      }
    } finally {
      setComputing(false);
    }
  }, [selected, inputs, t, lang]);

  // Detect when user has edited away from canonical
  useEffect(() => {
    if (canonicalLoaded && selected && !isCanonicalEqual(inputs, selected.canonical)) {
      setCanonicalLoaded(false);
    }
  }, [inputs, canonicalLoaded, selected]);

  // ---------------------------------------------------------------------------
  // Deep-link handler — Apr 2026
  //
  // URL contract (PRINT-PERMANENT — these URLs go on QR codes and into
  // printed-book footnotes; they MUST resolve correctly for the lifetime of
  // the printed edition):
  //
  //   /calculators/<calc_id>           → open detail, no prefill
  //   /calculators/<calc_id>?canonical=1 → open detail + prefill canonical + auto-compute
  //   /calculators/<unknown>           → fall back to grid (no error toast)
  //   /calculators                     → grid (no change)
  //
  // PT-BR mirror: /calculadoras/<calc_id>...  same semantics.
  //
  // Graceful-fallback rules (per Charles's guardrail):
  //   - unknown calc_id → silently render grid
  //   - canonical=1 on a calc with no canonical defined → open detail without
  //     prefill (no error)
  //   - any other malformed query string → ignored
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const urlKey = `${urlCalcId || ""}|${location.search || ""}`;
    if (lastAppliedUrlRef.current === urlKey) return;
    lastAppliedUrlRef.current = urlKey;
    if (!urlCalcId) return;
    const target = CALC_SCHEMA.find((c) => c.id === urlCalcId);
    if (!target) {
      // Unknown slug → silently render grid (no toast). The slug may have
      // come from a print-edition QR pointing to a renamed/removed calc.
      return;
    }
    const params = new URLSearchParams(location.search);
    const wantsCanonical = params.get("canonical") === "1";
    setSelectedId(target.id);
    if (wantsCanonical && target.canonical) {
      setInputs(deepCopy(target.canonical));
      setCanonicalLoaded(true);
      setPendingAutoCompute(true);
      // Lightweight analytics ping (best-effort, never blocks UX). The
      // endpoint may not exist yet — `.catch` swallows so the deep-link
      // still works on the printed-book URL contract.
      try {
        axios.post(`${API}/analytics/event`, {
          event: "deep_link_followed",
          calculator_id: target.id,
          canonical: true,
          lang,
        }, { withCredentials: true }).catch(() => {});
      } catch (_) { /* analytics is best-effort */ }
    }
  }, [urlCalcId, location.search, lang]);

  // Stable ref to compute() so the auto-compute effect doesn't re-fire
  // every render. We can't depend on `compute` directly because it's
  // declared with useCallback whose deps include `inputs`.
  const computeRef = useRef();
  useEffect(() => { computeRef.current = compute; }, [compute]);

  // Once the deep-link has prefilled inputs, fire compute exactly once.
  useEffect(() => {
    if (pendingAutoCompute && selected && inputsHaveContent(inputs) && !computing) {
      setPendingAutoCompute(false);
      computeRef.current?.();
    }
  }, [pendingAutoCompute, selected, inputs, computing]);

  // Sync URL ↔ selected calc so users can copy/share the URL of whichever
  // calc they have open. Replaces history (no back-button spam).
  useEffect(() => {
    if (lastAppliedUrlRef.current === null) return;
    const base = lang === "pt_br" ? "/calculadoras" : "/calculators";
    const desiredPath = selectedId ? `${base}/${selectedId}` : base;
    // Keep search params on detail views (e.g., ?canonical=1 from a deep-link)
    // but drop them on the grid since the canonical flag has no meaning there.
    const desiredSearch = selectedId ? location.search : "";
    if (location.pathname !== desiredPath || location.search !== desiredSearch) {
      navigate(desiredPath + desiredSearch, { replace: true });
    }
  }, [selectedId, lang, location.pathname, location.search, navigate]);

  if (!selected) return <CalcGrid lang={lang} t={t} onOpen={openCalc} />;

  return (
    <CalcDetail
      calc={selected}
      lang={lang}
      t={t}
      inputs={inputs}
      setInputs={setInputs}
      result={result}
      paywall={paywall}
      computing={computing}
      canonicalLoaded={canonicalLoaded}
      onCompute={compute}
      onLoadCanonical={loadCanonical}
      onBack={closeCalc}
      navigate={navigate}
    />
  );
};

// -----------------------------------------------------------------------------
// Grid view — landing page
// -----------------------------------------------------------------------------

const CalcGrid = ({ lang, t, onOpen }) => {
  const publicCalcs = CALC_SCHEMA.filter((c) => c.public);
  const gatedCalcs = CALC_SCHEMA.filter((c) => !c.public);

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="v11-calculators-page">
      <header className="border-b border-[#1E3A5F]/50 px-6 py-5">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-gradient-to-br from-[#1A6BCC] to-[#00C8E0] rounded-md flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold" data-testid="v11-page-title">{t.title}</h1>
          </div>
          <p className="text-sm text-slate-400 ml-12">{t.subtitle}</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-10">
        <Section title={t.publicSection} badge={t.publicBadge} badgeColor="emerald"
                 testid="v11-public-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {publicCalcs.map((c) => (
              <CalcCard key={c.id} calc={c} lang={lang} t={t} onClick={() => onOpen(c.id)} />
            ))}
          </div>
        </Section>

        <Section title={t.gatedSection} badge={t.gatedBadge} badgeColor="sky"
                 testid="v11-gated-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {gatedCalcs.map((c) => (
              <CalcCard key={c.id} calc={c} lang={lang} t={t} onClick={() => onOpen(c.id)} />
            ))}
          </div>
        </Section>
      </main>
    </div>
  );
};

// Calculator → source-chapter reverse-link map (per Charles's Pass-13 ACK).
// Used to show a "Read the worked example in Ch N" badge inside the
// calculator detail view so cold-from-QR users know where in the book
// the calculator lives. AET intentionally maps to TWO chapters
// (Ch 9 toxicology + Ch 14 combination products) per spec v1.2.
const CALC_TO_CHAPTERS = {
  basic_stats:        [{ ch: 1,  title_en: "Foundations & Worked Examples Showcase",
                                  title_pt_br: "Fundamentos e Vitrine de Exemplos Resolvidos" }],
  cpk_ppk:            [{ ch: 6,  title_en: "Process Capability — Cpk and Ppk",
                                  title_pt_br: "Capacidade do Processo — Cpk e Ppk" }],
  sigma_dpmo:         [{ ch: 12, title_en: "Six Sigma Toolset",
                                  title_pt_br: "Ferramentas Six Sigma" }],
  rss_stack:          [{ ch: 7,  title_en: "Tolerance Stack-Up Analysis",
                                  title_pt_br: "Análise de Empilhamento de Tolerâncias" }],
  weibull:            [{ ch: 8,  title_en: "Weibull Reliability",
                                  title_pt_br: "Confiabilidade de Weibull" }],
  arrhenius:          [{ ch: 10, title_en: "Accelerated Life Testing",
                                  title_pt_br: "Testes de Vida Acelerados" }],
  // Per spec v1.2 — AET appears in BOTH Ch 09 and Ch 14
  aet:                [
    { ch: 9,  title_en: "Safety and Toxicology",       title_pt_br: "Segurança e Toxicologia" },
    { ch: 14, title_en: "Combination Products",        title_pt_br: "Produtos de Combinação" },
  ],
  gage_rr:            [{ ch: 11, title_en: "Measurement System Analysis",
                                  title_pt_br: "Análise do Sistema de Medição" }],
  success_run:        [{ ch: 17, title_en: "Sampling and Verification",
                                  title_pt_br: "Amostragem e Verificação" }],
  oee:                [{ ch: 5,  title_en: "Manufacturing Operations and OEE",
                                  title_pt_br: "Operações de Manufatura e OEE" }],
  supplier_scorecard: [{ ch: 16, title_en: "Supplier Management",
                                  title_pt_br: "Gestão de Fornecedores" }],
};

const ChapterReverseLink = ({ calcId, lang, navigate }) => {
  const chapters = CALC_TO_CHAPTERS[calcId];
  if (!chapters || chapters.length === 0) return null;

  const labels = {
    en: { single: "Read the worked example in", multi: "Read the worked example in:",
          chapter: "Chapter" },
    pt_br: { single: "Leia o exemplo resolvido no", multi: "Leia o exemplo resolvido em:",
             chapter: "Capítulo" },
  }[lang];

  // Reader-hub doesn't yet have URL-based chapter routing; ship the badge as
  // visible context (tells the cold-from-QR reader where the worked example
  // lives in the book) plus a click that takes them to the dashboard.
  const goToBook = () => navigate("/reader-dashboard");

  if (chapters.length === 1) {
    const c = chapters[0];
    const titleField = lang === "pt_br" ? "title_pt_br" : "title_en";
    return (
      <button
        onClick={goToBook}
        className="mt-3 w-full text-left flex items-center gap-2 px-3 py-2 rounded bg-emerald-500/5 hover:bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-200 transition-colors"
        data-testid="v11-reverse-link-single"
      >
        <BookOpen className="w-3.5 h-3.5 flex-shrink-0" />
        <span>
          {labels.single} {labels.chapter} {c.ch}
          <span className="text-slate-400"> · {c[titleField]}</span>
        </span>
      </button>
    );
  }
  // Multi-chapter (AET only)
  return (
    <div
      className="mt-3 px-3 py-2 rounded bg-emerald-500/5 border border-emerald-500/30 text-xs"
      data-testid="v11-reverse-link-multi"
    >
      <div className="flex items-center gap-2 text-emerald-200 mb-1.5">
        <BookOpen className="w-3.5 h-3.5" /> <span>{labels.multi}</span>
      </div>
      <div className="space-y-1 ml-5">
        {chapters.map((c) => {
          const titleField = lang === "pt_br" ? "title_pt_br" : "title_en";
          return (
            <button
              key={c.ch}
              onClick={goToBook}
              className="block text-left text-emerald-300 hover:text-emerald-200 transition-colors"
              data-testid={`v11-reverse-link-multi-ch${c.ch}`}
            >
              {labels.chapter} {c.ch} <span className="text-slate-400">· {c[titleField]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Section = ({ title, badge, badgeColor, testid, children }) => {
  const c = COLOR_CLASSES[badgeColor];
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-4 border-b border-[#1E3A5F]/40 pb-2">
        <h2 className="text-xs font-bold text-slate-300 uppercase tracking-wider">{title}</h2>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.bg} ${c.text} border ${c.borderThin}`}>
          {badge}
        </span>
      </div>
      {children}
    </section>
  );
};

const CalcCard = ({ calc, lang, t, onClick }) => (
  <button
    onClick={onClick}
    className="text-left bg-[#0D1F38] border border-[#1E3A5F] hover:border-[#1A6BCC]/60 rounded-lg p-4 transition-colors group"
    data-testid={`v11-card-${calc.id}`}
  >
    <div className="flex items-start justify-between mb-3">
      <Calculator className="w-5 h-5 text-[#00C8E0]" />
      {calc.public ? (
        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
          {t.publicBadge}
        </span>
      ) : (
        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/20 flex items-center gap-1">
          <Lock className="w-2.5 h-2.5" /> {t.gatedBadge}
        </span>
      )}
    </div>
    <h3 className="text-sm font-bold text-white group-hover:text-[#00C8E0] transition-colors mb-1">
      {calc.title[lang]}
    </h3>
    <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-2">
      <BookOpen className="w-3 h-3" />
      <span>{calc.chapter_ref[lang]}</span>
    </div>
  </button>
);

// -----------------------------------------------------------------------------
// Detail view — per-calculator
// -----------------------------------------------------------------------------

const CalcDetail = ({ calc, lang, t, inputs, setInputs, result, paywall, computing,
                      canonicalLoaded, onCompute, onLoadCanonical, onBack, navigate }) => {
  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid={`v11-detail-${calc.id}`}>
      <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"
                  data-testid="v11-back-btn">
            <ArrowLeft className="w-4 h-4" /> {t.backToList}
          </button>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${calc.public ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" : "bg-sky-500/10 text-sky-300 border border-sky-500/20"}`}>
            {calc.public ? t.publicBadge : t.gatedBadge}
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[#132340] border border-[#1E3A5F] flex items-center justify-center">
            <Calculator className="w-5 h-5 text-[#00C8E0]" />
          </div>
          <h1 className="text-xl font-bold" data-testid="v11-calc-title">{calc.title[lang]}</h1>
        </div>
        <p className="text-xs text-slate-500 ml-13 flex items-center gap-1">
          <BookOpen className="w-3 h-3" /> {t.chapterRef} {calc.chapter_ref[lang]}
        </p>

        {paywall && <Paywall paywall={paywall} t={t} navigate={navigate} />}

        <div className={`mt-6 bg-[#0D1F38] rounded-lg p-5 transition-colors duration-300 ${
          canonicalLoaded
            ? "border-2 border-[#00C8E0]/60 ring-2 ring-[#00C8E0]/20"
            : "border border-[#1E3A5F]"
        }`}
             data-testid="v11-inputs-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t.inputsHeading}</h3>
            <button
              onClick={onLoadCanonical}
              className="text-xs px-3 py-1.5 rounded bg-[#00C8E0]/10 hover:bg-[#00C8E0]/20 text-[#00C8E0] border border-[#00C8E0]/30 flex items-center gap-1.5 transition-colors"
              data-testid="v11-canonical-chip"
            >
              <Sparkles className="w-3 h-3" /> {t.canonicalChip}
            </button>
          </div>

          {canonicalLoaded && (
            <div className="mb-4 px-3 py-2 rounded bg-[#00C8E0]/5 border border-[#00C8E0]/30 text-[11px] text-[#00C8E0] flex items-center gap-2"
                 data-testid="v11-canonical-banner">
              <Sparkles className="w-3 h-3 flex-shrink-0" />
              <span>{t.canonicalActive} <span className="font-bold">{calc.chapter_ref[lang]}</span> · <span className="text-slate-400">{t.canonicalEdit}</span></span>
            </div>
          )}

          <ChapterReverseLink calcId={calc.id} lang={lang} navigate={navigate} />

          <CalcInputs calc={calc} lang={lang} t={t} inputs={inputs} setInputs={setInputs} />

          <button
            onClick={onCompute}
            disabled={computing}
            className="mt-5 w-full bg-gradient-to-r from-[#1A6BCC] to-[#00C8E0] hover:opacity-90 disabled:opacity-40 text-white py-2.5 rounded font-bold text-sm flex items-center justify-center gap-2 transition-opacity"
            data-testid="v11-compute-btn"
          >
            {computing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
            {computing ? t.computing : t.compute}
          </button>
        </div>

        {result && !result.error && <ResultPanel calc={calc} result={result} lang={lang} t={t} />}
        {result?.error && (
          <div className="mt-4 bg-rose-900/10 border border-rose-800/30 rounded-lg p-3 text-rose-300 text-sm flex items-center gap-2"
               data-testid="v11-error-banner">
            <AlertTriangle className="w-4 h-4" /> {result.error}
          </div>
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Inputs renderer (handles standard fields, sigma_dpmo toggle,
// supplier_scorecard 5×2 grid, gage_rr nested variances)
// -----------------------------------------------------------------------------

const CalcInputs = ({ calc, lang, t, inputs, setInputs }) => {
  if (calc.id === "sigma_dpmo") return <SigmaDpmoInputs inputs={inputs} setInputs={setInputs} t={t} />;
  if (calc.id === "supplier_scorecard")
    return <SupplierScorecardInputs inputs={inputs} setInputs={setInputs} lang={lang} />;
  if (calc.nested === "variances")
    return <NestedInputs calc={calc} inputs={inputs} setInputs={setInputs} lang={lang} nestKey="variances" />;
  return <FlatInputs calc={calc} inputs={inputs} setInputs={setInputs} lang={lang} t={t} />;
};

const FlatInputs = ({ calc, inputs, setInputs, lang, t }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    {(calc.fields || []).map((f) => (
      <FieldInput key={f.key} field={f} value={inputs[f.key] ?? ""}
                  onChange={(v) => setInputs({ ...inputs, [f.key]: v })}
                  lang={lang} />
    ))}
    {calc.id === "success_run" && calc.presets && (
      <div className="sm:col-span-2 mt-1">
        <div className="text-[10px] text-slate-500 uppercase mb-2">{t.successRunPresets}</div>
        <div className="flex flex-wrap gap-2">
          {calc.presets.map((p) => (
            <button
              key={p.label}
              onClick={() => setInputs({ confidence: p.confidence, reliability: p.reliability })}
              className="px-3 py-1 rounded-full text-xs bg-[#1A6BCC]/15 hover:bg-[#1A6BCC]/30 text-[#00C8E0] border border-[#1A6BCC]/40 transition-colors"
              data-testid={`v11-preset-${p.label.replace("/", "-")}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    )}
  </div>
);

const NestedInputs = ({ calc, inputs, setInputs, lang, nestKey }) => {
  const nested = inputs[nestKey] || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {calc.fields.map((f) => (
        <FieldInput key={f.key} field={f} value={nested[f.key] ?? ""}
                    onChange={(v) => setInputs({ ...inputs, [nestKey]: { ...nested, [f.key]: v } })}
                    lang={lang} />
      ))}
    </div>
  );
};

const SigmaDpmoInputs = ({ inputs, setInputs, t }) => {
  const direction = inputs.sigma_level !== undefined && inputs.sigma_level !== ""
    ? "sigma_to_dpmo" : "dpmo_to_sigma";
  const setDirection = (dir) => {
    if (dir === "sigma_to_dpmo") setInputs({ sigma_level: inputs.sigma_level || "", apply_shift: inputs.apply_shift ?? true });
    else setInputs({ dpmo: inputs.dpmo || "", apply_shift: inputs.apply_shift ?? true });
  };
  return (
    <div>
      <div className="mb-3">
        <div className="text-[10px] text-slate-500 uppercase mb-2">{t.sigmaDirection}</div>
        <div className="flex gap-2">
          <button onClick={() => setDirection("sigma_to_dpmo")}
                  className={`px-3 py-1.5 rounded text-xs border transition-colors ${
                    direction === "sigma_to_dpmo"
                      ? "bg-[#1A6BCC] border-[#1A6BCC] text-white"
                      : "bg-transparent border-[#1E3A5F] text-slate-400 hover:border-[#1A6BCC]/50"}`}
                  data-testid="v11-sigma-direction-sigma">
            {t.sigmaToDpmo}
          </button>
          <button onClick={() => setDirection("dpmo_to_sigma")}
                  className={`px-3 py-1.5 rounded text-xs border transition-colors ${
                    direction === "dpmo_to_sigma"
                      ? "bg-[#1A6BCC] border-[#1A6BCC] text-white"
                      : "bg-transparent border-[#1E3A5F] text-slate-400 hover:border-[#1A6BCC]/50"}`}
                  data-testid="v11-sigma-direction-dpmo">
            {t.dpmoToSigma}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldInput
          field={{ key: direction === "sigma_to_dpmo" ? "sigma_level" : "dpmo", type: "number",
                   label: { en: direction === "sigma_to_dpmo" ? "Sigma level (0–9)" : "DPMO (0–999,999)",
                            pt_br: direction === "sigma_to_dpmo" ? "Nível Sigma (0–9)" : "DPMO (0–999.999)" } }}
          value={direction === "sigma_to_dpmo" ? (inputs.sigma_level ?? "") : (inputs.dpmo ?? "")}
          onChange={(v) => setInputs({ ...inputs,
            [direction === "sigma_to_dpmo" ? "sigma_level" : "dpmo"]: v })}
          lang="en"
        />
        <label className="flex items-center gap-2 self-end pb-2 text-xs text-slate-400">
          <input type="checkbox" checked={inputs.apply_shift ?? true}
                 onChange={(e) => setInputs({ ...inputs, apply_shift: e.target.checked })}
                 className="rounded border-[#1E3A5F]"
                 data-testid="v11-sigma-shift-toggle" />
          {t.sigmaShift}
        </label>
      </div>
    </div>
  );
};

const SUPPLIER_DIMS = ["quality", "delivery", "cost", "responsiveness", "compliance"];
const SUPPLIER_DIM_LABELS = {
  en: { quality: "Quality", delivery: "Delivery", cost: "Cost",
        responsiveness: "Responsiveness", compliance: "Compliance" },
  pt_br: { quality: "Qualidade", delivery: "Entrega", cost: "Custo",
           responsiveness: "Capacidade de Resposta", compliance: "Conformidade" },
};

const SupplierScorecardInputs = ({ inputs, setInputs, lang }) => {
  const scores = inputs.scores || {};
  const weights = inputs.weights || {};
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 text-[10px] text-slate-500 uppercase pb-1 border-b border-[#1E3A5F]/40">
        <div>Dimension</div>
        <div>Score (0–100)</div>
        <div>Weight (0–1)</div>
      </div>
      {SUPPLIER_DIMS.map((d) => (
        <div key={d} className="grid grid-cols-3 gap-2 items-center">
          <div className="text-xs text-slate-300">{SUPPLIER_DIM_LABELS[lang][d]}</div>
          <input type="number" step="any" value={scores[d] ?? ""}
                 onChange={(e) => setInputs({ ...inputs,
                   scores: { ...scores, [d]: e.target.value === "" ? "" : Number(e.target.value) } })}
                 className="bg-[#0A1628] border border-[#1E3A5F] rounded px-2 py-1.5 text-sm font-mono focus:border-[#1A6BCC] focus:outline-none"
                 data-testid={`v11-supplier-score-${d}`} />
          <input type="number" step="any" value={weights[d] ?? ""}
                 onChange={(e) => setInputs({ ...inputs,
                   weights: { ...weights, [d]: e.target.value === "" ? "" : Number(e.target.value) } })}
                 className="bg-[#0A1628] border border-[#1E3A5F] rounded px-2 py-1.5 text-sm font-mono focus:border-[#1A6BCC] focus:outline-none"
                 data-testid={`v11-supplier-weight-${d}`} />
        </div>
      ))}
    </div>
  );
};

const FieldInput = ({ field, value, onChange, lang }) => {
  const isCsv = field.type === "csv";
  return (
    <div>
      <label className="block text-[10px] text-slate-500 mb-1 uppercase">{field.label[lang]}</label>
      <input
        type={isCsv ? "text" : "number"}
        value={value === undefined || value === null ? "" : (typeof value === "object" ? JSON.stringify(value) : value)}
        step={field.step || "any"}
        placeholder={field.placeholder || ""}
        onChange={(e) => {
          const v = e.target.value;
          if (isCsv) onChange(v);
          else onChange(v === "" ? "" : Number(v));
        }}
        className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none font-mono"
        data-testid={`v11-input-${field.key}`}
      />
    </div>
  );
};

// -----------------------------------------------------------------------------
// Result panel (with calc-specific renderers)
// -----------------------------------------------------------------------------

const ResultPanel = ({ calc, result, lang, t }) => {
  const out = result.outputs || {};
  return (
    <div className="mt-4 space-y-3" data-testid="v11-result-panel">
      <div className="bg-[#0D1F38] border border-emerald-800/30 rounded-lg p-5">
        <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-3">{t.resultHeading}</h3>

        {calc.id === "cpk_ppk" && <CpkResult out={out} lang={lang} />}
        {calc.id === "supplier_scorecard" && <SupplierResult out={out} lang={lang} />}
        {calc.id === "arrhenius" && <ArrheniusResult out={out} lang={lang} />}
        {!["cpk_ppk", "supplier_scorecard", "arrhenius"].includes(calc.id) && (
          <GenericResult out={out} lang={lang} />
        )}
      </div>

      {(result.warnings || []).length > 0 && (
        <div className="bg-amber-900/10 border border-amber-800/30 rounded p-3 text-xs text-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>{(result.warnings || []).join(" · ")}</div>
        </div>
      )}

      {result.gated && (
        <div className="text-[10px] text-slate-500 flex items-center gap-2">
          <ShieldCheck className="w-3 h-3 text-sky-400" />
          {result.unlock_method === "role_bypass" ? "Admin / auditor access" : "Reader unlock"} ·
          gate {result.gate_chapter}
        </div>
      )}
    </div>
  );
};

const GenericResult = ({ out }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
    {Object.entries(out)
      .filter(([k, v]) => !k.endsWith("_pt_br") && typeof v !== "object" && v !== null)
      .filter(([k]) => !k.startsWith("interpretation_"))
      .map(([k, v]) => (
        <div key={k} className="bg-[#0A1628] rounded p-3">
          <div className="text-[10px] text-slate-500 uppercase">{k.replace(/_/g, " ")}</div>
          <div className="text-sm font-bold text-white font-mono mt-0.5">
            {typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 6 }) : String(v)}
          </div>
        </div>
      ))}
  </div>
);

const CpkResult = ({ out, lang }) => {
  const color = out.interpretation_color || CPK_COLOR(out.cpk);
  const c = COLOR_CLASSES[color] || COLOR_CLASSES.rose;
  const interp = lang === "pt_br" ? out.interpretation_pt_br : out.interpretation_en;
  return (
    <div className="space-y-3">
      <div className={`p-4 rounded-lg border-2 ${c.bg} ${c.border}`}>
        <div className={`text-xs uppercase tracking-wider ${c.text} font-bold mb-1`}>Cpk</div>
        <div className="text-3xl font-bold text-white font-mono">{out.cpk}</div>
        <div className="text-xs text-slate-300 mt-2">{interp}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["cpu", out.cpu], ["cpl", out.cpl], ["dpmo_estimate", out.dpmo_estimate],
          ...(out.ppk !== undefined ? [["ppk", out.ppk]] : [])].map(([k, v]) => (
          <div key={k} className="bg-[#0A1628] rounded p-2">
            <div className="text-[10px] text-slate-500 uppercase">{k.replace(/_/g, " ")}</div>
            <div className="text-sm font-bold text-white font-mono">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SupplierResult = ({ out, lang }) => {
  const tier = out.risk_tier || "";
  const color = TIER_COLOR(tier);
  const c = COLOR_CLASSES[color] || COLOR_CLASSES.sky;
  const tierLabel = lang === "pt_br" ? out.risk_tier_pt_br : out.risk_tier;
  const reco = lang === "pt_br" ? out.recommendation_pt_br : out.recommendation_en;
  const weak = lang === "pt_br" ? out.primary_weakness_pt_br : out.primary_weakness;
  return (
    <div className="space-y-3">
      <div className={`p-4 rounded-lg border-2 ${c.bg} ${c.border}`}>
        <div className={`text-xs uppercase tracking-wider ${c.text} font-bold mb-1`}>Composite</div>
        <div className="text-3xl font-bold text-white font-mono">{out.composite_score}</div>
        <div className="text-xs text-slate-300 mt-2"><span className="font-bold">{tierLabel}</span> · {reco}</div>
        <div className="text-[10px] text-slate-400 mt-1">Primary weakness: {weak}</div>
      </div>
      <div className="space-y-1">
        {(out.dimension_breakdown || []).map((d) => (
          <div key={d.dimension} className="flex items-center gap-2 text-xs">
            <div className="w-24 text-slate-400 capitalize">
              {lang === "pt_br" ? d.dimension_pt_br : d.dimension}
            </div>
            <div className="flex-1 bg-[#0A1628] rounded h-5 overflow-hidden border border-[#1E3A5F]">
              <div className="bg-gradient-to-r from-[#1A6BCC] to-[#00C8E0] h-full"
                   style={{ width: `${Math.min(100, d.score)}%` }} />
            </div>
            <div className="w-20 text-right font-mono">{d.score} · w {d.weight}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ArrheniusResult = ({ out, lang }) => {
  const interp = lang === "pt_br" ? out.interpretation_pt_br : out.interpretation_en;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#0A1628] rounded p-3 border border-[#1E3A5F]">
          <div className="text-[10px] text-slate-500 uppercase">Acceleration Factor</div>
          <div className="text-2xl font-bold text-[#00C8E0] font-mono">{out.acceleration_factor}×</div>
        </div>
        <div className="bg-[#0A1628] rounded p-3 border border-[#1E3A5F]">
          <div className="text-[10px] text-slate-500 uppercase">
            {lang === "pt_br" ? "Tempo de uso equivalente" : "Equivalent use time"} {/* translation-checker: allow — P0.4 (backend bilingual config) follow-up */}
          </div>
          <div className="text-2xl font-bold text-white font-mono">
            {out.equivalent_use_time !== null && out.equivalent_use_time !== undefined
              ? out.equivalent_use_time : "—"}
          </div>
        </div>
      </div>
      {/* Side-by-side Kelvin + Celsius (per Charles's UI nudge — Arrhenius is the
          calc most sensitive to °C-as-K user error; show both transparently). */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#0A1628] rounded p-3 border border-[#1E3A5F]">
          <div className="text-[10px] text-slate-500 uppercase mb-1">Use Temp</div>
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-bold font-mono">{out.temp_use_K}</span>
            <span className="text-[10px] text-slate-400">K</span>
            <span className="text-slate-600">|</span>
            <span className="text-sm font-mono text-slate-300">{(out.temp_use_K - 273.15).toFixed(2)}</span>
            <span className="text-[10px] text-slate-400">°C</span>
          </div>
        </div>
        <div className="bg-[#0A1628] rounded p-3 border border-[#1E3A5F]">
          <div className="text-[10px] text-slate-500 uppercase mb-1">Test Temp</div>
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-bold font-mono">{out.temp_accel_K}</span>
            <span className="text-[10px] text-slate-400">K</span>
            <span className="text-slate-600">|</span>
            <span className="text-sm font-mono text-slate-300">{(out.temp_accel_K - 273.15).toFixed(2)}</span>
            <span className="text-[10px] text-slate-400">°C</span>
          </div>
        </div>
      </div>
      <div className="text-xs text-slate-300 italic">{interp}</div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Paywall (rendered inline when API returns HTTP 402)
// -----------------------------------------------------------------------------

const Paywall = ({ paywall, t, navigate }) => (
  <div className="mt-5 bg-gradient-to-br from-sky-900/30 to-sky-700/10 border-2 border-sky-500/40 rounded-lg p-5"
       data-testid="v11-paywall">
    <div className="flex items-start gap-3">
      <Lock className="w-6 h-6 text-sky-300 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <h3 className="text-base font-bold text-white mb-1">{paywall.title}</h3>
        <p className="text-sm text-slate-300 mb-4">{paywall.message}</p>
        <button onClick={() => navigate(paywall.activationUrl)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded bg-sky-500 hover:bg-sky-400 text-white text-sm font-bold transition-colors"
                data-testid="v11-paywall-activate">
          {t.paywallActivate} <ExternalLink className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  </div>
);

export default V11CalculatorsPage;
